import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// Above-the-fold landing strings (~8KB). Statically imported so they ship
// in the entry chunk — every visitor gets the hero/nav/landingNew copy
// before any JSON network request happens. The other ~150KB of strings
// (dashboard, settings, etc.) still load on demand.
import enCritical from '@/shared/i18n/locales/en-critical.json';

/**
 * Detect user's preferred language synchronously (no network request).
 * Priority order (must match server/index.ts pickLandingVariant() exactly
 * so client and server agree on the same language for the first paint):
 *   1. midashLanguage in localStorage  (user's explicit in-app toggle)
 *   2. mi_lang cookie                  (last toggle, also seen by BFF)
 *   3. navigator.language               (browser/OS preference)
 *   4. 'en'                             (default)
 */
function detectLanguage(): 'en' | 'ar' {
  // 1. localStorage (user toggle inside the SPA)
  const stored = localStorage.getItem('midashLanguage');
  if (stored === 'ar' || stored === 'en') return stored;

  // 2. mi_lang cookie — written by i18n.changeLanguage so subsequent
  // visits get the matching pre-rendered variant from the BFF.
  if (typeof document !== 'undefined') {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)mi_lang=(en|ar)\b/);
    if (cookieMatch) return cookieMatch[1] as 'en' | 'ar';
  }

  // 3. navigator language preference
  const browserLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage || '';
  if (browserLang.toLowerCase().startsWith('ar')) return 'ar';

  return 'en';
}

/**
 * Load locale file dynamically
 * This splits the bundle - only the needed language is loaded
 */
async function loadLocale(lang: 'en' | 'ar') {
  if (lang === 'ar') {
    return (await import('@/shared/i18n/locales/ar.json')).default;
  }
  return (await import('@/shared/i18n/locales/en.json')).default;
}

/**
 * Synchronous i18n init — registers the engine + sets <html lang/dir>
 * WITHOUT waiting for the locale bundle. The locale is loaded in the
 * background by loadAndAddLocale() and merged in when it arrives.
 *
 * Why: awaiting the ~49KB locale fetch before React mount was the single
 * largest contributor to mobile LCP. Every t() call in the codebase
 * already passes a fallback string ("translated copy" pattern), so the
 * ~200-500ms window between mount and bundle-loaded renders correct
 * English copy. i18next fires a re-render automatically when
 * addResourceBundle is called.
 */
export function initI18n(): typeof i18n {
  const detectedLang = detectLanguage();

  // Set <html dir/lang> immediately for layout correctness — needs to
  // happen before React's first paint of any RTL-sensitive UI.
  const dir = detectedLang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', detectedLang);

  // Seed with the inlined critical subset for the detected language. For
  // English we have the real strings immediately; for Arabic the critical
  // subset is empty for now (the full bundle loads in the background and
  // re-renders consumers via i18next's resource event). The seed prevents
  // the brief flash of missing copy on the hero/nav above the fold.
  const seed = detectedLang === 'en' ? enCritical : {};
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: { [detectedLang]: { translation: seed } },
      lng: detectedLang,
      fallbackLng: 'en',
      debug: false,
      interpolation: { escapeValue: false },
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage'],
        lookupLocalStorage: 'midashLanguage',
      },
    });

  return i18n;
}

/**
 * Background-load the detected locale's translations and merge them into
 * the running i18n instance. Safe to call any time after initI18n().
 */
export async function loadAndAddLocale(): Promise<void> {
  const lang = detectLanguage();
  try {
    const translations = await loadLocale(lang);
    // Deep merge into the existing critical seed so any new keys
    // (dashboard, settings, etc.) become available, while critical
    // strings already in place are kept verbatim.
    i18n.addResourceBundle(lang, 'translation', translations, true, true);
  } catch (err) {
    console.warn('[i18n] Failed to load locale bundle:', err);
  }
}

// Re-introduced for compatibility — the old initI18n was async, some call
// sites may have awaited it. They now resolve immediately.
export async function initI18nAsync(): Promise<typeof i18n> {
  const inst = initI18n();
  await loadAndAddLocale();
  return inst;
}

// Keep <html dir/lang> in sync when the user switches language at runtime.
// Also persist a `mi_lang` cookie so the Express BFF can serve the matching
// pre-rendered variant on the user's next visit. Without this, an Arabic
// user who toggled language would keep getting the EN pre-rendered HTML
// on every page refresh (since the BFF can't read localStorage server-side).
i18n.on('languageChanged', (lng) => {
  const dir = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
  if (typeof document !== 'undefined' && (lng === 'en' || lng === 'ar')) {
    // 1-year persistence, top-level path so every route gets the right variant.
    // SameSite=Lax keeps it sent on top-level nav from external links (e.g.
    // someone clicking through from a Google result), which is exactly what
    // we want for the variant-routing path.
    document.cookie = `mi_lang=${lng}; max-age=31536000; path=/; SameSite=Lax`;
  }
});

// Preload the target language's bundle BEFORE switching so users never see
// the brief flash of key-fallbacks during a language toggle.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng?: string, callback?: (err: unknown, t: unknown) => void) => {
  if (lng === 'en' || lng === 'ar') {
    if (!i18n.hasResourceBundle(lng, 'translation') ||
        Object.keys(i18n.getResourceBundle(lng, 'translation') || {}).length === 0) {
      const newTranslations = await loadLocale(lng);
      i18n.addResourceBundle(lng, 'translation', newTranslations, true, true);
    }
  }
  return originalChangeLanguage(lng, callback);
};

export default i18n;
